/**
 * Client-side timeline for one session start: from the launch click, through
 * the create response, socket connection, hydration and each server startup
 * phase, to the first ready (or error) status. Marks are logged to the console
 * as they land and posted once to the API so they sit in the session journal
 * beside the server's own `bridge/startup_timing` frame.
 *
 * Two follow-on timelines share the machinery: a `resume` timeline runs from
 * the wake request to ready, and an `interaction` timeline records when the
 * composer became usable, when the first prompt was sent, and when the first
 * agent token arrived, all measured from the same launch click.
 */

import { api } from '@web/api/client'

export type StartupMark = { name: string; at_ms: number }
export type TimelineKind = 'startup' | 'resume' | 'interaction'

export type StartupLaunch = {
  source: string
  kind: 'startup' | 'resume'
  launchUnixMs: number
  origin: number
  sessionId: string | null
  marks: StartupMark[]
  reported: boolean
  /** Marks after ready, posted once the first agent token arrives. */
  interaction: StartupMark[]
  interactionReported: boolean
}

const MAX_MARKS = 64
const launches = new Map<string, StartupLaunch>()
/** Finished startups still waiting for their first prompt and token. */
const interactions = new Map<string, StartupLaunch>()
let pending: StartupLaunch | null = null

const clock = () =>
  typeof performance !== 'undefined' ? performance.now() : Date.now()

const elapsed = (launch: StartupLaunch) =>
  Math.round((clock() - launch.origin) * 10) / 10

function push(launch: StartupLaunch, name: string) {
  if (launch.reported || launch.marks.length >= MAX_MARKS) return
  launch.marks.push({ name, at_ms: elapsed(launch) })
}

function timeline(
  source: string,
  kind: 'startup' | 'resume',
  sessionId: string | null,
): StartupLaunch {
  return {
    source,
    kind,
    launchUnixMs: Date.now(),
    origin: clock(),
    sessionId,
    marks: [],
    reported: false,
    interaction: [],
    interactionReported: false,
  }
}

/** Start a timeline when the user asks for a new session, before its id exists. */
export function beginLaunch(source: string): void {
  pending = timeline(source, 'startup', null)
  push(pending, 'launch_click')
}

/** Start a resume timeline when this tab wakes a sleeping session. */
export function beginResume(sessionId: string, source: string): void {
  if (launches.has(sessionId)) return
  const launch = timeline(source, 'resume', sessionId)
  push(launch, 'resume_request')
  launches.set(sessionId, launch)
  interactions.delete(sessionId)
}

/** Record a mark on the launch that has not received its session id yet. */
export function markLaunch(name: string): void {
  if (pending) push(pending, name)
}

/** Bind the pending launch to the session the server created for it. */
export function adoptLaunch(sessionId: string): void {
  if (!pending) return
  pending.sessionId = sessionId
  launches.set(sessionId, pending)
  pending = null
}

/** Record a mark for a session whose start this tab initiated; ignore others. */
export function markStartup(sessionId: string, name: string): void {
  const launch = launches.get(sessionId)
  if (launch) push(launch, name)
}

/**
 * Record a post-ready mark (composer usable, first prompt, first token) for a
 * session this tab started. Each name is kept once; the batch is posted when
 * the first agent token arrives.
 */
export function markInteraction(sessionId: string, name: string): void {
  const launch = interactions.get(sessionId)
  if (!launch || launch.interactionReported) return
  if (launch.interaction.some((mark) => mark.name === name)) return
  if (launch.interaction.length >= MAX_MARKS) return
  launch.interaction.push({ name, at_ms: elapsed(launch) })
  if (name === 'first_token') {
    launch.interactionReported = true
    interactions.delete(sessionId)
    console.info(
      `[canopy startup] ${sessionId} first token at +${launch.interaction.at(-1)?.at_ms}ms`,
      Object.fromEntries(
        launch.interaction.map((mark) => [mark.name, mark.at_ms]),
      ),
    )
    void api
      .reportClientEvents(sessionId, {
        kind: 'interaction',
        launch_unix_ms: launch.launchUnixMs,
        events: launch.interaction,
      })
      .catch(() => undefined)
  }
}

/** Log the server's own timing frame next to the client marks for devtools reading. */
export function logServerTiming(
  sessionId: string,
  timing: Record<string, unknown>,
): void {
  const launch = launches.get(sessionId)
  if (!launch) return
  console.info(
    `[canopy startup] ${sessionId} server timing at +${elapsed(launch)}ms`,
    timing,
  )
}

/**
 * Close the timeline once, log it, and post it to the session journal.
 * Reporting failures are swallowed: telemetry must never break a session.
 */
export function finishStartup(
  sessionId: string,
  outcome: 'ready' | 'error',
): StartupLaunch | null {
  const launch = launches.get(sessionId)
  if (!launch || launch.reported) return null
  push(launch, outcome)
  launch.reported = true
  launches.delete(sessionId)
  if (outcome === 'ready') interactions.set(sessionId, launch)
  const total = launch.marks[launch.marks.length - 1]?.at_ms ?? 0
  console.info(
    `[canopy startup] ${sessionId} ${outcome} after ${total}ms (${launch.kind}, ${launch.source})`,
    Object.fromEntries(launch.marks.map((mark) => [mark.name, mark.at_ms])),
  )
  void api
    .reportClientEvents(sessionId, {
      kind: launch.kind,
      launch_unix_ms: launch.launchUnixMs,
      events: launch.marks,
    })
    .catch(() => undefined)
  return launch
}

const isAgentToken = (frame: Record<string, unknown>) => {
  const update = (frame.update ?? frame.params) as
    | Record<string, unknown>
    | undefined
  const kind = update?.sessionUpdate ?? update?.session_update ?? frame.kind
  return kind === 'agent_message_chunk' || kind === 'agent_message'
}

/** Observe one durable frame for a session this tab launched or woke. */
export function observeStartupFrame(
  sessionId: string,
  frame: Record<string, unknown> & { type: string },
): void {
  if (interactions.has(sessionId) && !launches.has(sessionId)) {
    if (frame.type.startsWith('agent/') && isAgentToken(frame))
      markInteraction(sessionId, 'first_token')
    return
  }
  if (!launches.has(sessionId)) return
  switch (frame.type) {
    case 'bridge/hello':
      markStartup(sessionId, 'hello')
      return
    case 'bridge/history_complete':
      markStartup(sessionId, 'history_complete')
      return
    case 'bridge/startup':
      if (typeof frame.phase === 'string')
        markStartup(sessionId, `phase:${frame.phase}`)
      return
    case 'bridge/startup_timing':
      markStartup(sessionId, 'server_timing')
      logServerTiming(sessionId, frame)
      return
    case 'bridge/session_status': {
      const status = String(frame.status ?? '')
      if (status === 'ready' || status === 'running')
        finishStartup(sessionId, 'ready')
      else if (status === 'error') finishStartup(sessionId, 'error')
      else if (status) markStartup(sessionId, `status:${status}`)
      return
    }
    default:
      return
  }
}

/** Test hook: inspect or clear in-memory timelines. */
export const startupTelemetryInternals = {
  launches,
  interactions,
  pending: () => pending,
  reset() {
    launches.clear()
    interactions.clear()
    pending = null
  },
}
